/**
 * Wahoo Cloud API — PKCE OAuth2 helpers
 *
 * Registration: https://developers.wahooligan.com/
 * Add your Redirect URI: http://localhost:5173/oauth/wahoo/callback
 * Then set VITE_WAHOO_CLIENT_ID in .env.local
 */

import { supabase } from "@/lib/supabase/client";

const CLIENT_ID = import.meta.env.VITE_WAHOO_CLIENT_ID as string | undefined;
const REDIRECT_URI = `${window.location.origin}/oauth/wahoo/callback`;
const AUTH_URL = "https://api.wahooligan.com/oauth/authorize";
// Token exchange proxied through our Netlify function to avoid CORS
const TOKEN_URL = "/.netlify/functions/wahoo-token";

// Per-user token isolation — set by authStore on login/logout so two different
// users on the same browser never share each other's Wahoo credentials.
let _userId: string | null = null;

export function setWahooUserId(id: string | null): void {
  _userId = id;
}

function storageKey(): string {
  return _userId ? `wahoo_token_${_userId}` : "wahoo_token";
}

export type WahooToken = {
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // unix ms
  userEmail?: string;
};

/**
 * Returns the current Supabase session JWT for use as a Bearer token in
 * Netlify function auth headers. Returns an empty string if no session exists.
 */
export async function getSupabaseJwt(): Promise<string> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? "";
  } catch {
    return "";
  }
}

// ── PKCE helpers ──────────────────────────────────────────────────────────────

export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export async function buildAuthUrl(verifier: string): Promise<string> {
  if (!CLIENT_ID)
    throw new Error("VITE_WAHOO_CLIENT_ID is not set in .env.local");
  const challenge = await generateCodeChallenge(verifier);
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: "code",
    scope: "workouts_read workouts_write plans_read plans_write user_read offline_data",
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  return `${AUTH_URL}?${params.toString()}`;
}

/**
 * Revokes ALL tokens for this app+user via the wahoo-api Netlify proxy.
 * Direct browser calls to api.wahooligan.com are blocked by CORS, so we
 * route through the proxy just like all other Wahoo API calls.
 * Failures are silently ignored — the token exchange proceeds regardless.
 */
export async function deauthorizeWahoo(accessToken: string): Promise<void> {
  try {
    const res = await fetch("/.netlify/functions/wahoo-api", {
      method: "POST",
      headers: {
        "x-wahoo-path": "permissions",
        "x-wahoo-method": "DELETE",
        "x-wahoo-token": accessToken,
        "Authorization": `Bearer ${await getSupabaseJwt()}`,
      },
    });
    if (!res.ok) {
      console.warn(`[Wahoo] deauthorize returned ${res.status}:`, await res.text());
    }
  } catch (err) {
    console.error("[Wahoo] Revocation failed:", err);
  }
}

export async function exchangeCodeForToken(
  code: string,
  verifier: string,
): Promise<WahooToken> {
  // Revoke any existing token first so we don't hit Wahoo's per-user token limit.
  // If the stored token is expired, refresh it to get a valid one for revocation.
  const existing = getWahooToken();
  if (existing) {
    let accessToken = existing.accessToken;
    if (!isTokenValid(existing)) {
      try {
        const refreshed = await refreshWahooToken(existing);
        accessToken = refreshed.accessToken;
      } catch {
        // Refresh failed (possibly at token limit). Keep the existing (expired)
        // access token — Wahoo's DELETE /v1/permissions accepts it for revocation,
        // breaking the deadlock.
      }
    }
    await deauthorizeWahoo(accessToken);
    clearWahooToken();
  }

  const res = await fetch(TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getSupabaseJwt()}` },
    body: JSON.stringify({ code, codeVerifier: verifier, redirectUri: REDIRECT_URI }),
  });
  if (!res.ok) {
    const text = await res.text();
    if (text.includes("Too many unrevoked access tokens")) {
      throw new Error(
        "Wahoo has too many active sessions for your account. " +
        "Open the Wahoo ELEMNT app → Profile → Connected Apps → remove Momentum Peaks, then try again.",
      );
    }
    throw new Error(`Wahoo token exchange failed: ${res.status} ${text}`);
  }
  const json = (await res.json()) as {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    user?: { email?: string };
  };
  const token: WahooToken = {
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresAt: Date.now() + json.expires_in * 1000,
    userEmail: json.user?.email,
  };
  persistToken(token);
  return token;
}

export async function refreshWahooToken(
  token: WahooToken,
): Promise<WahooToken> {
  const res = await fetch(TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getSupabaseJwt()}` },
    body: JSON.stringify({ refreshToken: token.refreshToken }),
  });
  if (!res.ok) {
    // Clear the stored token on definitive auth failures.
    // 401/403 = rejected credentials; 400 on a token refresh endpoint virtually
    // always means an expired/revoked refresh token (invalid_grant), not a bad
    // request format. Network errors (5xx, timeouts) are transient — wiping the
    // token would disconnect the user permanently for no reason.
    if (res.status === 401 || res.status === 403 || res.status === 400) {
      clearWahooToken();
    }
    throw new Error(`Wahoo token refresh failed: ${res.status}`);
  }
  const json = (await res.json()) as {
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };
  const refreshed: WahooToken = {
    accessToken: json.access_token,
    refreshToken: json.refresh_token ?? token.refreshToken,
    expiresAt: Date.now() + json.expires_in * 1000,
    userEmail: token.userEmail,
  };
  persistToken(refreshed);
  return refreshed;
}

// ── Token persistence ─────────────────────────────────────────────────────────

function persistToken(token: WahooToken): void {
  localStorage.setItem(storageKey(), JSON.stringify(token));
}

export function getWahooToken(): WahooToken | null {
  // Try the user-specific key first; fall back to the legacy "wahoo_token" key
  // for tokens that were stored before per-user isolation was added.
  const raw =
    localStorage.getItem(storageKey()) ??
    (_userId ? localStorage.getItem("wahoo_token") : null);
  if (!raw) return null;
  try {
    const token = JSON.parse(raw) as WahooToken;
    // Migrate legacy key to user-specific key so future reads use the right slot.
    if (_userId && !localStorage.getItem(storageKey())) {
      localStorage.setItem(storageKey(), raw);
      localStorage.removeItem("wahoo_token");
    }
    return token;
  } catch {
    return null;
  }
}

/** Returns true if we have a token that is still valid (or can be silently refreshed) */
export function isTokenValid(token: WahooToken): boolean {
  // Give a 60-second buffer so we don't use a token that expires mid-request
  return token.expiresAt - 60_000 > Date.now();
}

export function clearWahooToken(): void {
  localStorage.removeItem(storageKey());
}

/**
 * Returns a valid access token, refreshing it first if needed.
 * Throws if there is no stored token at all.
 */
export async function getValidAccessToken(): Promise<string> {
  const token = getWahooToken();
  if (token && token.accessToken !== "server-managed") {
    const migrated = await refreshWahooToken(token);
    return migrated.accessToken;
  }
  if (!token) throw new Error("Not connected to Wahoo — please connect first.");
  if (isTokenValid(token)) return token.accessToken;
  const refreshed = await refreshWahooToken(token);
  return refreshed.accessToken;
}
